import { Heart, Star, Sun, Droplet, Leaf, User } from "lucide-react";
import usePageSEO from "@/hooks/usePageSEO";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Link } from "react-router-dom";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Helmet } from "react-helmet";

const About = () => {
  usePageSEO({
    title: "Bio Beauté Zen | Votre institut à Montpellier",
    description: "Découvrez Bio Beauté Zen et Valérie, esthéticienne passionnée. Un institut tourné vers le bien-être, l'éco-responsabilité et les soins bio à Montpellier.",
    canonical: "https://biobeautezen.com/institut-beaute-bio-montpellier",
    ogTitle: "Bio Beauté Zen | Votre institut de beauté bio à Montpellier",
    ogDescription: "Découvrez Bio Beauté Zen et Valérie, esthéticienne passionnée. Un institut tourné vers le bien-être, l'éco-responsabilité et les soins bio."
  });

  const instituteImages = [
    {
      src: "/images/institut/institut-6.jpg",
      alt: "Entrée et enseigne Bio Beauté Zen",
    },
    {
      src: "/images/institut/institut-2.jpg",
      alt: "Espace accueil institut Bio Beauté Zen",
    },
    {
      src: "/images/institut/institut-4.jpg",
      alt: "Ambiance cosy et naturelle",
    },
    {
      src: "/images/institut/institut-1.jpg",
      alt: "Ambiance zen et apaisante en cabine",
    },
    {
      src: "/images/institut/institut-3.jpg",
      alt: "Produit cire d'épilation Fille au pluriel",
    },
    {
      src: "/images/institut/institut-5.jpg",
      alt: "Carte de visite et accueil Bio Beauté Zen",
    },
  ];
  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Accueil",
                "item": "https://biobeautezen.com/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Bio Beauté Zen",
                "item": "https://biobeautezen.com/institut-beaute-bio-montpellier"
              }
            ]
          })}
        </script>
      </Helmet>
      <div className="pt-20 pb-4">
        <div className="container mx-auto px-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/">Accueil</Link>
              </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Bio Beauté Zen</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </div>
      <main className="pb-16" role="main">
        {/* Hero Section */}
        <section className="bg-bbz-beige/40 py-16 md:py-20" aria-labelledby="heading-bio-beaute-zen">
          <div className="container mx-auto px-4 text-center">
            <h1
              id="heading-bio-beaute-zen"
              className="font-playfair text-4xl md:text-5xl font-semibold text-bbz-text mb-6"
            >
              Bio Beauté Zen
            </h1>
            <p className="text-lg md:text-xl max-w-3xl mx-auto">
              Découvrez l'histoire, les valeurs et la philosophie qui animent l'institut.
            </p>
          </div>
        </section>

        {/* Notre Histoire */}
        <section className="section-container" aria-labelledby="heading-rencontrez-valerie">
          <div className="max-w-5xl mx-auto">
            <h2 id="heading-rencontrez-valerie" className="section-title">
              Rencontrez Valérie
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-[42%_58%] gap-4 items-stretch">
              <div className="w-full h-full">
                <img
                  src="/images/portrait-valerie.jpg"
                  alt="Portrait de Valérie, esthéticienne chez Bio Beauté Zen à Montpellier"
                  loading="lazy"
                  className="rounded-lg shadow-md w-full max-h-[420px] object-cover object-[center_5%]"
                />
              </div>
              <div className="prose max-w-none md:pl-2">
                <p className="mb-4">Esthéticienne passionnée, authentique et humaine.</p>
                <p className="mb-4">
                  Originaire du Centre de la France, j'ai choisi Montpellier pour y construire ma vie et me former à
                  l'esthétique. Après l'obtention de mon BTS, CAP et Brevet de Maîtrise, je dirige un premier institut,
                  avant de faire une pause pour me consacrer à ma famille.
                </p>
                <p className="mb-4">
                  En 2017, je reprends l'institut Bio Beauté Zen avec l'envie profonde de créer un lieu à mon image :
                  chaleureux, apaisant et tourné vers le bien-être.
                </p>
                <p>
                  Lauréate du prix Jeune Créateur aux Beauty Forum Awards 2019, je propose des soins bio, sincères et
                  sur-mesure. Ce métier est bien plus qu'un travail : c'est une manière d'être présente pour les autres,
                  avec écoute, savoir-faire et bienveillance.
                </p>
              </div>
            </div>

            {/* Carrousel de photos de l'institut */}
            <div className="mt-16 w-full px-4 md:px-8 lg:px-12 animate-fade-in">
              <h3 className="text-center font-playfair text-2xl md:text-3xl font-medium text-bbz-text mb-8">
                L'institut en images
              </h3>

              <Carousel
                opts={{
                  align: "start",
                  loop: true,
                  skipSnaps: false,
                  dragFree: true,
                  containScroll: "trimSnaps",
                }}
                className="w-full"
              >
                <CarouselContent className="-ml-2 md:-ml-4">
                  {instituteImages.map((image, index) => (
                    <CarouselItem key={index} className="pl-2 md:pl-4 basis-full md:basis-1/2 lg:basis-1/4">
                      <div className="relative overflow-hidden rounded-lg shadow-md">
                        <img
                          src={image.src}
                          alt={image.alt}
                          className="w-full aspect-square object-cover select-none"
                          loading="lazy"
                          draggable="false"
                        />
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>

                <CarouselPrevious className="hidden md:flex -left-4 lg:-left-12 bg-background/90 hover:bg-bbz-green/20 border-bbz-green text-bbz-text" />
                <CarouselNext className="hidden md:flex -right-4 lg:-right-12 bg-background/90 hover:bg-bbz-green/20 border-bbz-green text-bbz-text" />
              </Carousel>
            </div>
          </div>
        </section>

        {/* Nos Valeurs */}
        <section className="section-container" aria-labelledby="heading-mes-valeurs">
          <div className="max-w-5xl mx-auto">
            <h2 id="heading-mes-valeurs" className="section-title text-center mx-auto after:mx-auto">
              Mes valeurs
            </h2>
            <p className="text-center mb-10">Les principes qui guident chacune de mes actions et chacun de mes choix</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
              <div className="flex gap-4 items-start">
                <div className="bg-bbz-green/30 p-3 rounded-full">
                  <Sun className="w-6 h-6 text-bbz-text" />
                </div>
                <div>
                  <h3 className="font-playfair text-xl font-medium mb-2">Authenticité</h3>
                  <p>
                    Je privilégie la transparence et l'honnêteté dans mes conseils et mes soins. Mon approche est sincère
                    et mes résultats durables.
                  </p>
                </div>
              </div>

              <div className="flex gap-4 items-start">
                <div className="bg-bbz-green/30 p-3 rounded-full">
                  <Leaf className="w-6 h-6 text-bbz-text" />
                </div>
                <div>
                  <h3 className="font-playfair text-xl font-medium mb-2">Éco-responsabilité</h3>
                  <p>
                    Ma démarche vise à réduire mon empreinte écologique au quotidien : marques françaises et locales,
                     emballages recyclables, consommation d'énergie maîtrisée.
                  </p>
                </div>
              </div>

              <div className="flex gap-4 items-start">
                <div className="bg-bbz-green/30 p-3 rounded-full">
                  <Droplet className="w-6 h-6 text-bbz-text" />
                </div>
                <div>
                  <h3 className="font-playfair text-xl font-medium mb-2">Naturalité</h3>
                  <p>
                    Je sélectionne des ingrédients purs, sans substances nocives, respectueux de votre peau, de votre
                    santé et sans compromis sur l'efficacité.
                  </p>
                </div>
              </div>

              <div className="flex gap-4 items-start">
                <div className="bg-bbz-green/30 p-3 rounded-full">
                  <User className="w-6 h-6 text-bbz-text" />
                </div>
                <div>
                  <h3 className="font-playfair text-xl font-medium mb-2">Bienveillance</h3>
                  <p>
                    Chaque client est unique et mérite une attention personnalisée. Je prends le temps d'écouter, de
                    comprendre et d'adapter mes soins.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Nos Engagements */}
        <section className="section-container" aria-labelledby="heading-mes-engagements">
          <div className="bg-bbz-pink/20 rounded-lg p-8 md:p-10">
            <h2 id="heading-mes-engagements" className="section-title">
              Mes engagements
            </h2>
            <p className="mb-6">
              Chez Bio Beauté Zen, je m'engage à vous offrir une expérience bien-être complète dans le respect de
              l'environnement et de votre santé.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ul className="leaf-bullet space-y-3">
                <li>Proposer une routine de beauté minimaliste et sans artifices</li>
                <li>Créer un environnement déconnecté et propice à une détente optimale</li>
                <li>Garantir une transparence totale sur mes produits et méthodes</li>
              </ul>

              <ul className="leaf-bullet space-y-3">
                <li>Utiliser essentiellement des produits biologiques et naturels</li>
                <li>Offrir une expérience sur mesure, unique et adaptée à vos besoins</li>
                <li>Proposer des soins adaptés à toutes les peaux, même les plus sensibles</li>
              </ul>
            </div>
          </div>
        </section>
        <div className="text-center mt-12 container mx-auto px-4">
          <p className="text-lg mb-6 text-foreground">
            Envie de découvrir mes soins ?{" "}
            <Link to="/soins-visage-massages-epilations-montpellier" className="text-bbz-green-dark hover:underline font-semibold">
              Consultez ma carte des soins
            </Link>{" "}
            ou{" "}
            <Link to="/contact-institut-montpellier" className="text-bbz-green-dark hover:underline font-semibold">
              prenez rendez-vous directement
            </Link>
          </p>
        </div>
      </main>
    </>
  );
};
export default About;
